import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, X } from "lucide-react";
import { Button } from "../ui/button";
import { hasWorkspaceAccess, type WorkspaceId } from "../../lib/workspaces";
import { useAuth } from "../../context/AuthContext";

type TourStep = {
  title: string;
  description: string;
  path: string;
  workspace?: WorkspaceId;
};

const ROLE_TOURS: Record<string, TourStep[]> = {
  owner_admin: [
    { title: "Your operating command center", description: "Start in My Day to review visits, commissions, claims, and work requiring your decision.", path: "/my-day", workspace: "my_day" },
    { title: "Finish tenant setup", description: "Review company details, branches, users, workflow defaults, readiness, and setup-summary documents.", path: "/onboarding" },
    { title: "Manage people and login documents", description: "Create users, assign roles and access, issue temporary logins, and review redacted credential history.", path: "/admin/users" },
    { title: "Control the workflow", description: "Confirm terminology, statuses, commission rules, mileage, expenses, invoicing, and tenant settings.", path: "/config" },
    { title: "Keep the team self-sufficient", description: "Publish approved guides and confirm each role can open its quick-start material.", path: "/training", workspace: "training" },
  ],
  manager_trainer: [
    { title: "Your operating queue", description: "Use My Day to review assigned work and management items requiring attention.", path: "/my-day", workspace: "my_day" },
    { title: "Verify field activity", description: "Review planned versus completed visits and approve or reject the evidence within your authority.", path: "/visit-verification", workspace: "visit_verification" },
    { title: "Coach with accountability", description: "Open Coaching to document development, follow-up, and assigned staff progress.", path: "/coaching", workspace: "coaching" },
    { title: "Review team performance", description: "Use User 360 only for staff you are authorized to review.", path: "/user-360", workspace: "user_360" },
    { title: "Use the approved playbooks", description: "Training holds the tenant's current operating instructions.", path: "/training", workspace: "training" },
  ],
  admin_support: [
    { title: "Start with today's work", description: "My Day brings together your assigned tasks and operational follow-ups.", path: "/my-day", workspace: "my_day" },
    { title: "Maintain referral progress", description: "Use Referrals to add patients and update referral status within your role.", path: "/referrals", workspace: "referrals" },
    { title: "Keep records current", description: "Use Leads and Organizations for the operational records available to you.", path: "/contacts", workspace: "contacts" },
    { title: "Follow the approved process", description: "Training contains the tenant's current work instructions and quick-start guides.", path: "/training", workspace: "training" },
  ],
  representative: [
    { title: "Your day starts here", description: "My Day shows the tasks, follow-ups, visits, and approvals relevant to your work.", path: "/my-day", workspace: "my_day" },
    { title: "Work your assigned leads", description: "Use Leads to record activity, profiling notes, referrals, and the next action.", path: "/contacts", workspace: "contacts" },
    { title: "Plan and prove the work", description: "Use Task Tracker and Calendar for scheduled activity, then complete the required visit details.", path: "/tasks", workspace: "tasks" },
    { title: "Know your numbers", description: "My Performance shows your pipeline, conversions, commissions, mileage, and related records.", path: "/my-performance", workspace: "my_performance" },
    { title: "Use the playbook", description: "Open Training whenever you need the approved process or troubleshooting guide.", path: "/training", workspace: "training" },
  ],
  viewer: [
    { title: "Your read-only workspace", description: "The navigation shows only information your Owner has allowed you to review.", path: "/" },
    { title: "Review the dashboard", description: "Use Dashboard for the tenant overview when it is included in your access.", path: "/dashboard", workspace: "dashboard" },
    { title: "Open approved training", description: "Training provides the current reference material without granting operational edit access.", path: "/training", workspace: "training" },
  ],
};

function tourKey(user: any): string {
  const identity = user?._id || user?.id || user?.email || "unknown";
  const tenant = user?.tenant_id || user?.tenant?.id || "tenant";
  const role = user?.tenant_role || "user";
  return `rk:first-login-tour:v1:${tenant}:${identity}:${role}`;
}

export function RoleFirstLoginTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const steps = useMemo(() => {
    if (!user || user.scope === "platform") return [];
    const roleSteps = ROLE_TOURS[user.tenant_role] || ROLE_TOURS.viewer;
    return roleSteps.filter((step) => !step.workspace || hasWorkspaceAccess(user, step.workspace));
  }, [user]);

  useEffect(() => {
    if (!user || !steps.length) return;
    const saved = window.localStorage.getItem(tourKey(user));
    if (saved === "complete") return;
    const parsed = Number(saved);
    setIndex(Number.isInteger(parsed) && parsed >= 0 && parsed < steps.length ? parsed : 0);
    setOpen(true);
  }, [user, steps.length]);

  if (!user || user.scope === "platform" || !steps.length) return null;

  const finish = () => {
    window.localStorage.setItem(tourKey(user), "complete");
    setOpen(false);
  };
  const goNext = () => {
    if (index >= steps.length - 1) {
      finish();
      return;
    }
    const next = index + 1;
    window.localStorage.setItem(tourKey(user), String(next));
    setIndex(next);
  };
  const visit = () => {
    navigate(steps[index].path);
    goNext();
  };
  const restart = () => {
    setIndex(0);
    window.localStorage.setItem(tourKey(user), "0");
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={restart}
        className="fixed bottom-4 right-4 z-30 flex items-center gap-2 rounded-full border border-sky-200 bg-white px-4 py-2 text-sm font-medium text-sky-800 shadow-lg hover:bg-sky-50"
        aria-label="Restart quick tour"
      >
        <Compass className="h-4 w-4" /> Quick tour
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/50 p-4" role="presentation">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="role-tour-title"
            className="w-full max-w-lg rounded-2xl border border-sky-100 bg-white p-6 shadow-2xl"
            data-testid="role-first-login-tour"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">
                  {user.tenant_role?.replaceAll("_", " ")} quick start
                </p>
                <h2 id="role-tour-title" className="mt-1 text-xl font-semibold text-slate-950">
                  {steps[index].title}
                </h2>
              </div>
              <button type="button" onClick={finish} aria-label="Close tour" className="rounded-md p-2 text-slate-500 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="mt-3 text-sm leading-6 text-slate-600">{steps[index].description}</p>
            <div className="mt-5 h-2 overflow-hidden rounded-full bg-slate-100" aria-label={`Tour step ${index + 1} of ${steps.length}`}>
              <div className="h-full bg-sky-600 transition-all" style={{ width: `${((index + 1) / steps.length) * 100}%` }} />
            </div>
            <p className="mt-2 text-xs text-slate-500">Step {index + 1} of {steps.length}. Only workspaces currently available to you are included.</p>

            <div className="mt-6 flex flex-wrap justify-between gap-3">
              <Button type="button" variant="ghost" onClick={finish}>Skip tour</Button>
              <div className="flex gap-2">
                {index > 0 && <Button type="button" variant="outline" onClick={() => setIndex(index - 1)}>Back</Button>}
                <Button type="button" variant="outline" onClick={goNext}>{index === steps.length - 1 ? "Finish" : "Next"}</Button>
                <Button type="button" onClick={visit}>Open workspace</Button>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
